export function findFallbackNodesForRule(ctx, ruleKey, documentNode, isEditorContext) {
	if (!isEditorContext) {
		return [];
	}

	const details = ctx.rulesViewRuleMetadataByKey?.[ruleKey]?.details;

	if (!details || typeof details !== 'object') {
		return [];
	}

	if (ruleKey === 'linkTitle') {
		return findFallbackLinkNodes(ctx, details, documentNode);
	}

	if (ruleKey === 'headingOrder') {
		return findFallbackHeadingNodes(ctx, details, documentNode);
	}

	if (ruleKey === 'imageAlt') {
		return findFallbackImageNodes(ctx, details, documentNode);
	}

	if (ruleKey === 'formLabels') {
		return findFallbackFormControlNodes(ctx, details, documentNode);
	}

	if (ruleKey === 'keyboardFocusable') {
		return findFallbackKeyboardNodes(ctx, details, documentNode);
	}

	if (ruleKey === 'accessibleNamePreferredSource') {
		return findFallbackAccessibleNamePreferredSourceNodes(ctx, details, documentNode);
	}

	if (ruleKey === 'ariaLabelledbyIntegrity') {
		return findFallbackAriaLabelledbyNodes(ctx, details, documentNode);
	}

	if (ruleKey === 'ariaHiddenInteractive') {
		return findFallbackAriaHiddenInteractiveNodes(ctx, details, documentNode);
	}

	if (ruleKey === 'lowColorContrast') {
		return findFallbackLowContrastNodes(ctx, details, documentNode);
	}

	return [];
}

export function findFallbackLinkNodes(ctx, details, documentNode) {
	const invalidLinks = Array.isArray(details.invalidLinks) ? details.invalidLinks : [];
	const anchorNodes = Array.from(documentNode.querySelectorAll('a[href]'));
	const matches = new Set();

	for (const linkItem of invalidLinks) {
		if (!ctx.isPagePartSelector('linkTitle', linkItem?.selector || '')) {
			continue;
		}

		const targetHref = ctx.normalizeComparableUrl(linkItem?.href || '', documentNode);
		const targetText = ctx.normalizeComparableText(linkItem?.text || '');

		for (const anchorNode of anchorNodes) {
			const href = ctx.normalizeComparableUrl(anchorNode.getAttribute('href') || '', documentNode);
			const text = ctx.normalizeComparableText(anchorNode.textContent || '');
			const hrefMatch = targetHref && href && targetHref === href;
			const textMatch = targetText && text && targetText === text;

			if ((targetHref && targetText && hrefMatch && textMatch) || (targetHref && !targetText && hrefMatch) || (!targetHref && targetText && textMatch)) {
				matches.add(anchorNode);
			}
		}
	}

	return ctx.filterEditorEligibleNodes(Array.from(matches));
}

export function findFallbackHeadingNodes(ctx, details, documentNode) {
	const failedItems = Array.isArray(details.failedItems) ? details.failedItems : [];
	const matches = new Set();

	for (const item of failedItems) {
		if (!ctx.isPagePartSelector('headingOrder', item?.selector || '')) {
			continue;
		}

		const tag = typeof item?.tag === 'string' && item.tag.length > 0 ? item.tag : 'h1, h2, h3, h4, h5, h6';
		const targetText = ctx.normalizeComparableText(item?.text || '');
		const candidates = Array.from(documentNode.querySelectorAll(tag));

		for (const candidate of candidates) {
			const text = ctx.normalizeComparableText(candidate.textContent || '');
			if (targetText && text === targetText) {
				matches.add(candidate);
			}
		}
	}

	return ctx.filterEditorEligibleNodes(Array.from(matches));
}

export function findFallbackImageNodes(ctx, details, documentNode) {
	const invalidImages = Array.isArray(details.invalidImages) ? details.invalidImages : [];
	const imageNodes = Array.from(documentNode.querySelectorAll('img'));
	const matches = new Set();

	for (const imageItem of invalidImages) {
		if (!ctx.isPagePartSelector('imageAlt', imageItem?.selector || '')) {
			continue;
		}

		const targetSrc = ctx.normalizeComparableUrl(imageItem?.src || '', documentNode);
		const targetAlt = ctx.normalizeComparableText(imageItem?.alt || '');

		for (const imageNode of imageNodes) {
			const src = ctx.normalizeComparableUrl(imageNode.getAttribute('src') || '', documentNode);
			const alt = ctx.normalizeComparableText(imageNode.getAttribute('alt') || '');
			const srcMatch = targetSrc && src && targetSrc === src;
			const altMatch = targetAlt && alt && targetAlt === alt;

			if ((targetSrc && srcMatch) || (!targetSrc && targetAlt && altMatch)) {
				matches.add(imageNode);
			}
		}
	}

	return ctx.filterEditorEligibleNodes(Array.from(matches));
}

export function findFallbackFormControlNodes(ctx, details, documentNode) {
	const invalidControls = Array.isArray(details.invalidControls) ? details.invalidControls : [];
	const matches = new Set();

	for (const control of invalidControls) {
		if (!ctx.isPagePartSelector('formLabels', control?.selector || '')) {
			continue;
		}

		if (control?.id) {
			const byId = documentNode.getElementById(control.id);
			if (byId) {
				matches.add(byId);
				continue;
			}
		}

		if (control?.name) {
			const byName = Array.from(documentNode.querySelectorAll(`[name="${ctx.escapeSelectorValue(control.name)}"]`));
			for (const node of byName) {
				matches.add(node);
			}
			if (byName.length > 0) {
				continue;
			}
		}

		const tagSelector = control?.tag && control?.type && control.tag === 'input'
			? `${control.tag}[type="${ctx.escapeSelectorValue(control.type)}"]`
			: (control?.tag || 'input, select, textarea');
		const candidates = Array.from(documentNode.querySelectorAll(tagSelector));

		for (const candidate of candidates) {
			matches.add(candidate);
		}
	}

	return ctx.filterEditorEligibleNodes(Array.from(matches));
}

export function findFallbackKeyboardNodes(ctx, details, documentNode) {
	const issues = Array.isArray(details.issues) ? details.issues : [];
	const matches = new Set();

	for (const issue of issues) {
		if (!ctx.isPagePartSelector('keyboardFocusable', issue?.selector || '')) {
			continue;
		}

		if (issue?.id) {
			const byId = documentNode.getElementById(issue.id);
			if (byId) {
				matches.add(byId);
				continue;
			}
		}

		const tag = issue?.tag || '*';
		const candidates = Array.from(documentNode.querySelectorAll(tag));

		for (const candidate of candidates) {
			const nameMatch = issue?.name ? candidate.getAttribute('name') === issue.name : true;
			const roleMatch = issue?.role ? candidate.getAttribute('role') === issue.role : true;

			if (nameMatch && roleMatch) {
				matches.add(candidate);
			}
		}
	}

	return ctx.filterEditorEligibleNodes(Array.from(matches));
}

export function findFallbackAriaLabelledbyNodes(ctx, details, documentNode) {
	const issues = Array.isArray(details.issues) ? details.issues : [];
	const matches = new Set();

	for (const issue of issues) {
		if (!ctx.isPagePartSelector('ariaLabelledbyIntegrity', issue?.selector || '')) {
			continue;
		}

		if (issue?.id) {
			const byId = documentNode.getElementById(issue.id);
			if (byId) {
				matches.add(byId);
				continue;
			}
		}

		const labelledByValue = String(issue?.ariaLabelledby || '').trim();
		if (labelledByValue.length > 0) {
			const attrMatches = Array.from(documentNode.querySelectorAll(`[aria-labelledby="${ctx.escapeSelectorValue(labelledByValue)}"]`));
			for (const node of attrMatches) {
				matches.add(node);
			}
			if (attrMatches.length > 0) {
				continue;
			}
		}

		const allCandidates = Array.from(documentNode.querySelectorAll('[aria-labelledby]'));
		for (const candidate of allCandidates) {
			matches.add(candidate);
		}
	}

	return ctx.filterEditorEligibleNodes(Array.from(matches));
}

export function findFallbackAccessibleNamePreferredSourceNodes(ctx, details, documentNode) {
	const issues = Array.isArray(details.issues) ? details.issues : [];
	const matches = new Set();

	for (const issue of issues) {
		if (!ctx.isPagePartSelector('accessibleNamePreferredSource', issue?.selector || '')) {
			continue;
		}

		if (issue?.id) {
			const byId = documentNode.getElementById(issue.id);
			if (byId) {
				matches.add(byId);
				continue;
			}
		}

		if (issue?.name) {
			const byName = Array.from(documentNode.querySelectorAll(`[name="${ctx.escapeSelectorValue(issue.name)}"]`));
			for (const node of byName) {
				matches.add(node);
			}
			if (byName.length > 0) {
				continue;
			}
		}

		if (issue?.ariaLabel) {
			const byAriaLabel = Array.from(documentNode.querySelectorAll(`[aria-label="${ctx.escapeSelectorValue(issue.ariaLabel)}"]`));
			for (const node of byAriaLabel) {
				matches.add(node);
			}
			if (byAriaLabel.length > 0) {
				continue;
			}
		}

		const tag = issue?.tag || '*';
		const tagCandidates = Array.from(documentNode.querySelectorAll(tag));
		for (const candidate of tagCandidates) {
			matches.add(candidate);
		}
	}

	return ctx.filterEditorEligibleNodes(Array.from(matches));
}

export function findFallbackAriaHiddenInteractiveNodes(ctx, details, documentNode) {
	const issues = Array.isArray(details.issues) ? details.issues : [];
	const matches = new Set();

	for (const issue of issues) {
		if (!ctx.isPagePartSelector('ariaHiddenInteractive', issue?.selector || '')) {
			continue;
		}

		if (issue?.id) {
			const byId = documentNode.getElementById(issue.id);
			if (byId) {
				matches.add(byId);
				continue;
			}
		}

		if (issue?.role) {
			const byRole = Array.from(documentNode.querySelectorAll(`[role="${ctx.escapeSelectorValue(issue.role)}"]`));
			for (const node of byRole) {
				matches.add(node);
			}
			if (byRole.length > 0) {
				continue;
			}
		}

		const tag = issue?.tag || '*';
		const candidates = Array.from(documentNode.querySelectorAll(tag));

		for (const candidate of candidates) {
			matches.add(candidate);
		}
	}

	return ctx.filterEditorEligibleNodes(Array.from(matches));
}

export function findFallbackLowContrastNodes(ctx, details, documentNode) {
	const issues = getLowContrastIssueItems(ctx, details);
	const matches = new Set();
	const lowContrastDiagnostics = {
		issuesCount: issues.length,
		issueTextSamples: issues.slice(0, 5).map((issue) => ctx.normalizeComparableText(issue?.text || '').slice(0, 140)),
		candidateSampleCount: 0,
		candidateTextSamples: []
	};

	const allCandidates = ctx.filterEditorEligibleNodes(Array.from(documentNode.querySelectorAll('*')));
	lowContrastDiagnostics.candidateSampleCount = allCandidates.length;
	lowContrastDiagnostics.candidateTextSamples = allCandidates
		.map((candidate) => ctx.normalizeComparableText(candidate.textContent || ''))
		.filter((text) => text.length >= 20)
		.slice(0, 10);

	for (const issue of issues) {
		if (!ctx.isPagePartSelector('lowColorContrast', issue?.selector || '')) {
			continue;
		}

		const targetText = ctx.normalizeComparableText(issue?.text || '');
		const selectorTokens = extractSelectorTokens(ctx, issue?.selector || '');
		const candidates = allCandidates;
		let matchedByText = false;

		for (const candidate of candidates) {
			const text = ctx.normalizeComparableText(candidate.textContent || '');

			if (!targetText) {
				continue;
			}

			const isDirectMatch = text.includes(targetText) || targetText.includes(text);
			const isFuzzyMatch = hasStrongTokenOverlap(ctx, targetText, text, 2);
			const selectorHintMatch = matchesSelectorTokenHints(ctx, candidate, selectorTokens);

			if (isDirectMatch || isFuzzyMatch || selectorHintMatch) {
				matches.add(candidate);
				matchedByText = true;
			}
		}

		if (!matchedByText && typeof issue?.selector === 'string' && issue.selector.length > 0) {
			const selectorTailMatches = findNodesBySelectorTail(ctx, issue.selector, documentNode);

			for (const selectorNode of selectorTailMatches) {
				matches.add(selectorNode);
			}
		}
	}

	ctx.lastFallbackDiagnosticsByRule = ctx.lastFallbackDiagnosticsByRule || {};
	ctx.lastFallbackDiagnosticsByRule.lowColorContrast = lowContrastDiagnostics;

	return ctx.filterEditorEligibleNodes(Array.from(matches));
}

export function getLowContrastIssueItems(_ctx, details) {
	if (!details || typeof details !== 'object') {
		return [];
	}

	if (Array.isArray(details.issues) && details.issues.length > 0) {
		return details.issues;
	}

	if (!Array.isArray(details.checks)) {
		return [];
	}

	return details.checks.filter((item) => item?.isFail === true);
}

export function extractSelectorTokens(_ctx, selector) {
	if (typeof selector !== 'string' || selector.length === 0) {
		return [];
	}

	const tokenMatches = selector.toLowerCase().match(/[a-z][a-z0-9_-]{2,}/g);

	if (!Array.isArray(tokenMatches)) {
		return [];
	}

	const ignore = new Set(['nth', 'type', 'child', 'first', 'last', 'only', 'not']);

	return Array.from(new Set(tokenMatches.filter((token) => !ignore.has(token)))).slice(0, 8);
}

export function matchesSelectorTokenHints(_ctx, node, tokens) {
	if (!node || !Array.isArray(tokens) || tokens.length === 0) {
		return true;
	}

	const tagText = (node.tagName || '').toLowerCase();
	const idText = (node.id || '').toLowerCase();
	const classText = (typeof node.className === 'string' ? node.className : '').toLowerCase();

	for (const token of tokens) {
		if (tagText === token || idText.includes(token) || classText.includes(token)) {
			return true;
		}
	}

	return false;
}

export function getRuleFallbackDiagnostics(ctx, ruleKey) {
	if (!ctx.lastFallbackDiagnosticsByRule || typeof ctx.lastFallbackDiagnosticsByRule !== 'object') {
		return null;
	}

	return ctx.lastFallbackDiagnosticsByRule[ruleKey] || null;
}

export function findNodesBySelectorTail(_ctx, selector, documentNode) {
	if (!selector || !documentNode) {
		return [];
	}

	const parts = selector.split('>').map((part) => part.trim()).filter(Boolean);

	if (parts.length === 0) {
		return [];
	}

	const tailSelectors = [];

	if (parts.length >= 1) {
		tailSelectors.push(parts[parts.length - 1]);
	}

	if (parts.length >= 2) {
		tailSelectors.push(`${parts[parts.length - 2]} > ${parts[parts.length - 1]}`);
	}

	const matches = new Set();

	for (const tailSelector of tailSelectors) {
		let nodes = [];

		try {
			nodes = Array.from(documentNode.querySelectorAll(tailSelector));
		} catch {
			nodes = [];
		}

		for (const node of nodes) {
			matches.add(node);
		}
	}

	return Array.from(matches);
}

export function hasStrongTokenOverlap(_ctx, targetText, candidateText, minimumMatches = 3) {
	if (!targetText || !candidateText) {
		return false;
	}

	const tokenize = (text) => text
		.split(/[^a-z0-9]+/)
		.map((token) => token.trim())
		.filter((token) => token.length >= 4);

	const targetTokens = Array.from(new Set(tokenize(targetText)));
	const candidateTokens = new Set(tokenize(candidateText));
	let overlapCount = 0;

	for (const token of targetTokens) {
		if (candidateTokens.has(token)) {
			overlapCount += 1;
		}
	}

	return overlapCount >= minimumMatches;
}
